import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, Button, Avatar, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Snackbar, Tooltip, InputBase, alpha, Pagination, Skeleton,
} from '@mui/material';
import {
  Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon,
  ContentCopy as CopyIcon, QrCode2 as QrIcon,
  CalendarToday as CalendarIcon, Search as SearchIcon,
  CreditCard as CardIcon, OpenInNew as OpenIcon,
  WarningAmber as WarningIcon, Download as DownloadIcon,
} from '@mui/icons-material';
import QRCode from 'qrcode';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { fmtDate } from '../utils/dateFormat';
import { toDisplayCard } from '../utils/cardDataStructure';
import logoImg from '../assets/webcard-icon.png';

const PRIMARY = '#00A7E3';
const PLAN_CARD_LIMITS = { free: 1, pro: 1, business: 5 };
const PAGE_SIZE = 7;

function MyCardsListSkeleton({ rows = PAGE_SIZE }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {[...Array(rows)].map((_, i) => (
        <Card key={`card-sk-${i}`} variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'stretch', minHeight: 88 }}>
            <Skeleton variant="rectangular" width={4} sx={{ bgcolor: `${PRIMARY}55` }} animation="wave" />
            <Box sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
              <Skeleton variant="circular" width={48} height={48} animation="wave" />
            </Box>
            <Box sx={{ flex: 1, py: 1.5, pr: 1 }}>
              <Skeleton variant="text" width="38%" height={22} animation="wave" />
              <Skeleton variant="text" width="62%" height={18} sx={{ mt: 0.75 }} animation="wave" />
              <Skeleton variant="text" width="55%" height={16} sx={{ mt: 1 }} animation="wave" />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 2 }}>
              {[1, 2, 3, 4, 5].map((j) => (
                <Skeleton key={j} variant="circular" width={32} height={32} animation="wave" />
              ))}
            </Box>
          </Box>
        </Card>
      ))}
    </Box>
  );
}

const mapApiCards = (list) =>
  (list || []).map((card) => {
    const raw = card.cardData ?? card.card_data;
    let parsed = raw;
    if (typeof raw === 'string') {
      try {
        parsed = JSON.parse(raw);
      } catch {
        parsed = {};
      }
    }
    const display = toDisplayCard(parsed || {});
    const numericTemplateId =
      card.templateId ?? card.template_id;
    const templateIdNum =
      numericTemplateId != null && numericTemplateId !== ''
        ? Number(numericTemplateId)
        : NaN;
    const templateId =
      Number.isFinite(templateIdNum) && templateIdNum > 0
        ? templateIdNum
        : null;
    return {
      ...display,
      id: card.id,
      customUrl: card.cardUrl ?? card.card_url,
      templateId,
      templateName: card.templateName ?? card.template_name ?? '',
      createdAt: card.createdAt ?? card.created_at,
      isActive: card.isActive ?? card.is_active,
      isPublic: card.isPublic ?? card.is_public,
      ownerUsername: card.ownerUsername ?? card.owner_username,
      ownerEmail: card.ownerEmail ?? card.owner_email,
      profileImageUrl: display.profileImageUrl || '',
      name: display.name || 'Untitled Card',
      title: display.title || '',
      company: display.company || '',
      email: display.email || '',
      phone: display.phone || '',
    };
  });

const generateBrandedQr = (text, size = 400) => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    QRCode.toCanvas(canvas, text, {
      width: size, margin: 2, errorCorrectionLevel: 'H',
      color: { dark: '#000000', light: '#ffffff' },
    }, (err) => {
      if (err) { reject(err); return; }
      const ctx = canvas.getContext('2d');
      const logo = new Image();
      logo.crossOrigin = 'anonymous';
      logo.onload = () => {
        const logoSize = size * 0.22;
        const x = (size - logoSize) / 2;
        const y = (size - logoSize) / 2;
        const pad = 6;
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        const r = 8;
        ctx.moveTo(x - pad + r, y - pad);
        ctx.arcTo(x + logoSize + pad, y - pad, x + logoSize + pad, y + logoSize + pad, r);
        ctx.arcTo(x + logoSize + pad, y + logoSize + pad, x - pad, y + logoSize + pad, r);
        ctx.arcTo(x - pad, y + logoSize + pad, x - pad, y - pad, r);
        ctx.arcTo(x - pad, y - pad, x + logoSize + pad, y - pad, r);
        ctx.closePath();
        ctx.fill();
        ctx.drawImage(logo, x, y, logoSize, logoSize);
        resolve(canvas.toDataURL('image/png'));
      };
      logo.onerror = () => resolve(canvas.toDataURL('image/png'));
      logo.src = logoImg;
    });
  });
};

const MyCards = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const firstLoadRef = useRef(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, cardId: null, cardName: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [qrDialog, setQrDialog] = useState({ open: false, card: null, qrSrc: null });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const loadCardsPage = async (p) => {
    try {
      if (firstLoadRef.current) setLoading(true);
      else setListLoading(true);
      const { data } = await api.get('/cards/my-cards', { params: { page: p, limit: PAGE_SIZE } });
      let rows = data.cards || [];
      let pag = data.pagination;
      if (p > 1 && rows.length === 0 && pag && (pag.total || 0) > 0) {
        const newPage = Math.max(1, p - 1);
        setPage(newPage);
        const { data: d2 } = await api.get('/cards/my-cards', { params: { page: newPage, limit: PAGE_SIZE } });
        rows = d2.cards || [];
        pag = d2.pagination;
      }
      setCards(mapApiCards(rows));
      setPagination(pag || null);
      setPage(p);
    } catch (err) { console.error('Error loading cards:', err); }
    finally {
      setLoading(false);
      setListLoading(false);
      firstLoadRef.current = false;
    }
  };

  const loadAllCards = async () => {
    try {
      if (firstLoadRef.current) setLoading(true);
      else setListLoading(true);
      const { data } = await api.get('/cards/my-cards');
      setCards(mapApiCards(data.cards));
      setPagination(null);
    } catch (err) { console.error('Error loading cards:', err); }
    finally {
      setLoading(false);
      setListLoading(false);
      firstLoadRef.current = false;
    }
  };

  useEffect(() => {
    if (debouncedSearch.trim()) {
      loadAllCards();
    } else {
      loadCardsPage(page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: load page vs full list by search mode
  }, [page, debouncedSearch]);

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`https://api.webcard.digital/api/cards/${deleteDialog.cardId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        if (debouncedSearch.trim()) {
          await loadAllCards();
        } else {
          await loadCardsPage(page);
        }
        setDeleteDialog({ open: false, cardId: null, cardName: '' });
        toast('Card deleted successfully');
      }
    } catch (err) { console.error('Delete error:', err); }
  };

  const toast = (msg) => setSnackbar({ open: true, message: msg });
  const copyLink = (url) => { navigator.clipboard.writeText(`https://app.webcard.digital/${url}`); toast('Link copied!'); };
  const viewCard = (url) => window.open(`/${url}`, '_blank');
  const editCard = (url) => {
    const c = cards.find((x) => x.customUrl === url);
    if (!c) return;
    const templateQ =
      c.templateId != null && c.templateId > 0
        ? String(c.templateId)
        : encodeURIComponent(c.templateName || 'Classic Professional');
    navigate(`/customize?template=${templateQ}&edit=${encodeURIComponent(url)}`);
  };

  const openQrDialog = async (card) => {
    setQrDialog({ open: true, card, qrSrc: null });
    try {
      const qr = await generateBrandedQr(`https://app.webcard.digital/${card.customUrl}`, 600);
      setQrDialog(prev => ({ ...prev, qrSrc: qr }));
    } catch { setQrDialog(prev => ({ ...prev, qrSrc: 'error' })); }
  };

  const downloadQr = () => {
    if (!qrDialog.qrSrc || qrDialog.qrSrc === 'error') return;
    const a = document.createElement('a');
    a.href = qrDialog.qrSrc;
    a.download = `qr-${qrDialog.card?.customUrl || 'card'}.png`;
    a.click();
  };

  const isAdmin = user?.role === 'admin';
  const plan = user?.plan === 'premium' ? 'pro' : (user?.plan || 'free');
  const cardLimit = PLAN_CARD_LIMITS[plan];
  const totalCards = pagination?.total ?? cards.length;
  const atLimit = cardLimit && !isAdmin && totalCards >= cardLimit;

  const filteredCards = cards.filter(c => {
    if (!debouncedSearch.trim()) return true;
    const q = debouncedSearch.toLowerCase();
    return [c.name, c.company, c.customUrl, c.templateName, c.ownerEmail, c.ownerUsername].some(f => (f || '').toLowerCase().includes(q));
  });

  const showNoSearchResults = totalCards > 0 && Boolean(debouncedSearch.trim()) && filteredCards.length === 0;

  return (
    <Box sx={{ maxWidth: 960, mx: 'auto', py: 4, px: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        {loading ? (
          <Skeleton variant="text" width={isAdmin ? 120 : 100} height={36} animation="wave" sx={{ borderRadius: 1 }} />
        ) : (
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {isAdmin ? 'All Cards' : 'My Cards'}
          </Typography>
        )}
        {loading ? (
          <Skeleton variant="rounded" width={160} height={32} animation="wave" sx={{ borderRadius: 2 }} />
        ) : atLimit ? (
          <Button variant="contained" size="small" onClick={() => navigate('/subscription')}
            sx={{ bgcolor: '#f59e0b', textTransform: 'none', fontWeight: 600, borderRadius: 2, boxShadow: 'none', '&:hover': { bgcolor: '#d97706', boxShadow: 'none' } }}>
            Upgrade for More Cards
          </Button>
        ) : (
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => navigate('/templates')}
            sx={{ bgcolor: PRIMARY, textTransform: 'none', fontWeight: 600, borderRadius: 2, boxShadow: 'none', '&:hover': { bgcolor: '#0094cc', boxShadow: 'none' } }}>
            Create Card
          </Button>
        )}
      </Box>
      {loading ? (
        <Skeleton variant="text" width={300} height={22} animation="wave" sx={{ mb: 3 }} />
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {totalCards === 0 ? 'Create your first digital business card.' : `${totalCards} card${totalCards !== 1 ? 's' : ''}`}
          {cardLimit && !isAdmin ? ` · ${cardLimit} allowed on ${plan} plan` : ''}
        </Typography>
      )}

      {/* Search */}
      {!loading && totalCards > 3 && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, px: 2, py: 1,
          bgcolor: t => alpha(t.palette.text.primary, 0.03), borderRadius: 2,
          border: '1px solid', borderColor: 'divider',
          '&:focus-within': { borderColor: 'primary.main', bgcolor: 'background.paper' },
        }}>
          <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
          <InputBase placeholder="Search cards..." value={searchQuery} onChange={(e) => {
            const v = e.target.value;
            setSearchQuery(v);
            if (!v.trim()) {
              setPage(1);
              setDebouncedSearch('');
            }
          }}
            sx={{ flex: 1, fontSize: '0.9rem' }} />
          {debouncedSearch.trim() && <Typography variant="caption" color="text.secondary">{filteredCards.length} found</Typography>}
        </Box>
      )}

      {/* Empty state / list */}
      {loading ? (
        <MyCardsListSkeleton rows={PAGE_SIZE} />
      ) : totalCards === 0 ? (
        <Box sx={{ textAlign: 'center', py: 10, borderRadius: 3, border: '2px dashed', borderColor: 'divider' }}>
          <CardIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>No cards yet</Typography>
          <Typography color="text.secondary" sx={{ mb: 3, maxWidth: 360, mx: 'auto' }}>
            Create your first digital business card and start sharing your professional identity instantly.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/templates')}
            sx={{ bgcolor: PRIMARY, textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 4, boxShadow: 'none', '&:hover': { bgcolor: '#0094cc' } }}>
            Create Your First Card
          </Button>
        </Box>
      ) : (
        <>
          {listLoading ? (
            <MyCardsListSkeleton rows={PAGE_SIZE} />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {showNoSearchResults ? (
                <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                  No cards match your search.
                </Typography>
              ) : (
                filteredCards.map(card => (
                  <CardRow key={card.id} card={card} isAdmin={isAdmin}
                    onView={viewCard} onEdit={editCard} onCopy={copyLink}
                    onDelete={(id, name) => setDeleteDialog({ open: true, cardId: id, cardName: name })}
                    onQr={openQrDialog} />
                ))
              )}
            </Box>
          )}
          {!listLoading && !debouncedSearch.trim() && pagination && pagination.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                color="primary"
                count={pagination.totalPages}
                page={page}
                onChange={(_, p) => setPage(p)}
                shape="rounded"
              />
            </Box>
          )}
        </>
      )}

      {/* QR Dialog */}
      <Dialog open={qrDialog.open} onClose={() => setQrDialog({ open: false, card: null, qrSrc: null })}
        PaperProps={{ sx: { borderRadius: 3, maxWidth: 380, width: '100%' } }}>
        <DialogTitle sx={{ textAlign: 'center', pb: 0, fontWeight: 700 }}>
          Card QR Code
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pt: 2 }}>
          {qrDialog.qrSrc && qrDialog.qrSrc !== 'error' ? (
            <Box component="img" src={qrDialog.qrSrc} alt="QR Code"
              sx={{ width: 240, height: 240, borderRadius: 2, border: '1px solid', borderColor: 'divider' }} />
          ) : qrDialog.qrSrc === 'error' ? (
            <Typography color="error">Failed to generate QR</Typography>
          ) : (
            <CircularProgress size={32} sx={{ my: 4 }} />
          )}
          <Typography variant="body2" sx={{ mt: 2, fontWeight: 600, color: 'text.primary' }}>
            {qrDialog.card?.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace', mt: 0.5 }}>
            app.webcard.digital/{qrDialog.card?.customUrl}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.5, lineHeight: 1.5 }}>
            Anyone who scans this QR code will be taken directly to your digital visiting card.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: 'center', gap: 1 }}>
          <Button onClick={() => { copyLink(qrDialog.card?.customUrl); }} variant="outlined" size="small"
            startIcon={<CopyIcon sx={{ fontSize: 16 }} />}
            sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}>
            Copy Link
          </Button>
          <Button onClick={downloadQr} variant="contained" size="small"
            startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
            disabled={!qrDialog.qrSrc || qrDialog.qrSrc === 'error'}
            sx={{ bgcolor: PRIMARY, textTransform: 'none', borderRadius: 2, fontWeight: 600, boxShadow: 'none', '&:hover': { bgcolor: '#0094cc', boxShadow: 'none' } }}>
            Download QR
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, cardId: null, cardName: '' })}
        PaperProps={{ sx: { borderRadius: 3, maxWidth: 380 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: t => alpha(t.palette.error.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <WarningIcon color="error" fontSize="small" />
          </Box>
          Delete Card
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete <strong>{deleteDialog.cardName}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialog({ open: false, cardId: null, cardName: '' })}
            sx={{ textTransform: 'none', borderRadius: 2 }}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained"
            sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600, boxShadow: 'none' }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} message={snackbar.message}
        onClose={() => setSnackbar({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{ sx: { borderRadius: 2, fontWeight: 500 } }} />
    </Box>
  );
};

// ═══════════════════════════════════════
//  Card Row — clean list layout
// ═══════════════════════════════════════
function CardRow({ card, isAdmin, onView, onEdit, onCopy, onDelete, onQr }) {
  const initial = card.name?.charAt(0)?.toUpperCase() || 'U';

  return (
    <Card variant="outlined" sx={{
      borderRadius: 2.5, overflow: 'hidden',
      transition: 'box-shadow 0.2s, border-color 0.2s',
      '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.06)', borderColor: `${PRIMARY}40` },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'stretch', minHeight: 88 }}>
        {/* Left color bar */}
        <Box sx={{ width: 4, bgcolor: PRIMARY, flexShrink: 0 }} />

        {/* Avatar */}
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
          <Avatar src={card.profileImageUrl}
            sx={{ width: 48, height: 48, bgcolor: `${PRIMARY}18`, color: PRIMARY, fontWeight: 700, fontSize: '1.1rem' }}>
            {initial}
          </Avatar>
        </Box>

        {/* Info */}
        <Box sx={{ flex: 1, py: 1.5, pr: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
            <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700, color: 'text.primary' }}>
              {card.name}
            </Typography>
            {card.templateName && (
              <Chip label={card.templateName} size="small"
                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, bgcolor: `${PRIMARY}12`, color: PRIMARY }} />
            )}
          </Box>

          {(card.title || card.company) && (
            <Typography variant="caption" noWrap sx={{ color: 'text.secondary', display: 'block', mb: 0.25 }}>
              {card.title}{card.title && card.company ? ' · ' : ''}{card.company}
            </Typography>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
            <Box onClick={() => onCopy(card.customUrl)}
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', '&:hover .url-text': { color: PRIMARY } }}>
              <Typography className="url-text" variant="caption"
                sx={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'text.disabled', transition: 'color 0.15s' }}>
                app.webcard.digital/{card.customUrl}
              </Typography>
              <CopyIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
            </Box>
            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
              {fmtDate(card.createdAt)}
            </Typography>
          </Box>

          {isAdmin && card.ownerEmail && (
            <Typography variant="caption" sx={{ color: 'info.main', fontSize: '0.7rem', mt: 0.25, display: 'block' }}>
              {card.ownerUsername && card.ownerUsername !== card.ownerEmail ? `${card.ownerUsername} · ` : ''}{card.ownerEmail}
            </Typography>
          )}
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: { xs: 1, sm: 2 }, flexShrink: 0 }}>
          <Tooltip title="View card" arrow>
            <IconButton size="small" onClick={() => onView(card.customUrl)}
              sx={{ color: PRIMARY, '&:hover': { bgcolor: `${PRIMARY}10` } }}>
              <OpenIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit card" arrow>
            <IconButton size="small" onClick={() => onEdit(card.customUrl)}
              sx={{ color: 'text.secondary', '&:hover': { bgcolor: t => alpha(t.palette.text.primary, 0.06) } }}>
              <EditIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download QR code" arrow>
            <IconButton size="small" onClick={() => onQr(card)}
              sx={{ color: 'text.secondary', '&:hover': { bgcolor: t => alpha(t.palette.text.primary, 0.06) } }}>
              <QrIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Copy link" arrow>
            <IconButton size="small" onClick={() => onCopy(card.customUrl)}
              sx={{ color: 'text.secondary', '&:hover': { bgcolor: t => alpha(t.palette.text.primary, 0.06) } }}>
              <CopyIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete" arrow>
            <IconButton size="small" onClick={() => onDelete(card.id, card.name)}
              sx={{ color: 'text.disabled', '&:hover': { color: 'error.main', bgcolor: t => alpha(t.palette.error.main, 0.08) } }}>
              <DeleteIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Card>
  );
}

export default MyCards;
